import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { getProductPin } from '../../context/ProductContext';

/**
 * The TinyCMS plans (Free, Personal, Business, Pro) as signup and Plan & billing
 * show them. Prices and features come from the server (Query.tinycmsPlans,
 * apiserver platform/services/plans.js) so the screens never disagree with
 * what an account is actually charged.
 */

export const TINYCMS_PLANS_QUERY = gql`
  query TinycmsPlans {
    tinycmsPlans {
      key
      label
      summary
      monthlyPrice
      yearlyPrice
      trialPeriodDays
      products
      features
      maxDomains
      maxMembers
    }
  }
`;

export type BillingInterval = 'month' | 'year';

export interface TinycmsPlan {
  key: string;
  label: string;
  summary: string;
  monthlyPrice: number;
  yearlyPrice: number;
  trialPeriodDays: number;
  products: string[];
  features: string[];
  maxDomains: number;
  maxMembers: number;
}

export function useTinycmsPlans() {
  const { data, loading, error } = useQuery<{ tinycmsPlans: TinycmsPlan[] }>(TINYCMS_PLANS_QUERY);
  return { plans: data?.tinycmsPlans || [], loading, error };
}

/** True on a TinyCMS host or build (app.tinycms.ai), where signup sells these plans. */
export const isTinycmsFunnel = () => getProductPin() === 'atomizer';

export const toInterval = (v: string | null | undefined): BillingInterval =>
  v === 'month' ? 'month' : 'year';

/** Per-month price for the interval. */
export const planPrice = (plan: TinycmsPlan, interval: BillingInterval) =>
  interval === 'year' ? plan.yearlyPrice : plan.monthlyPrice;

export const money = (n: number) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

/** "Free" · "$24/mo, billed monthly" · "$18/mo, billed yearly ($216)". */
export function billingLine(plan: TinycmsPlan, interval: BillingInterval) {
  const price = planPrice(plan, interval);
  if (!price) return 'Free';
  return interval === 'year'
    ? `${money(price)}/mo, billed yearly (${money(price * 12)})`
    : `${money(price)}/mo, billed monthly`;
}

/** What is charged when the trial ends: "$216 a year" or "$24 a month". */
export function chargeLine(plan: TinycmsPlan, interval: BillingInterval) {
  const price = planPrice(plan, interval);
  return interval === 'year' ? `${money(price * 12)} a year` : `${money(price)} a month`;
}

interface PickerProps {
  plans: TinycmsPlan[];
  selected: string;
  onSelect: (key: string) => void;
  interval: BillingInterval;
  onInterval: (interval: BillingInterval) => void;
  /** The account's current plan, marked on its card (Plan & billing). */
  current?: string | null;
  /** Show each paid plan's trial (signup only: a plan change starts no trial). */
  showTrial?: boolean;
}

export function TinycmsPlanPicker({
  plans,
  selected,
  onSelect,
  interval,
  onInterval,
  current,
  showTrial = true,
}: PickerProps) {
  return (
    <div>
      <div
        role="radiogroup"
        aria-label="Billing"
        className="inline-flex rounded-full border border-cmp-line-2 p-1 mb-5"
      >
        {(['month', 'year'] as BillingInterval[]).map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={interval === value}
            onClick={() => onInterval(value)}
            className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
              interval === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
            }`}
          >
            {value === 'month' ? 'Monthly' : 'Yearly · save 25%'}
          </button>
        ))}
      </div>
      <RadioGroup value={selected} onValueChange={onSelect}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((plan) => {
            const price = planPrice(plan, interval);
            return (
              <label
                key={plan.key}
                className={`relative cursor-pointer rounded-lg border transition-all ${
                  selected === plan.key
                    ? 'border-primary ring-2 ring-primary ring-offset-2'
                    : 'hover:border-primary/50'
                }`}
              >
                <Card className="border-0 p-1 bg-core-dark h-full">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <RadioGroupItem value={plan.key} id={`plan-${plan.key}`} />
                      {current === plan.key ? (
                        <span className="px-2 py-0.5 rounded-full border border-cmp-line-2 text-xs text-muted-foreground">
                          Current
                        </span>
                      ) : null}
                    </div>
                    <CardTitle>{plan.label}</CardTitle>
                    <div className="mt-3">
                      <span className="font-bold text-2xl">{price ? money(price) : '$0'}</span>
                      <span className="text-muted-foreground">/mo</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {!price
                        ? 'Free forever'
                        : interval === 'year'
                          ? `${money(price * 12)} billed yearly`
                          : 'Billed monthly'}
                    </p>
                    <CardDescription className="mt-2">{plan.summary}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    {showTrial && price && plan.trialPeriodDays ? (
                      <p className="text-xs text-muted-foreground mt-4">
                        {plan.trialPeriodDays}-day free trial
                      </p>
                    ) : null}
                  </CardContent>
                </Card>
              </label>
            );
          })}
        </div>
      </RadioGroup>
    </div>
  );
}
